import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../utils/LoadingSpinner';
import { FaSearch, FaSort, FaAndroid, FaSitemap } from 'react-icons/fa';
import deviceService from '../services/DeviceService';
import '../styles/Reports.css';
import '../styles/DeviceModal.css';
import InformationSection from './InformationSection';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { useUserList } from '../hooks/useUserList';
import { useSite } from './../context/SiteContext';
import Button from './ui/Button';
import Pagination from './ui/Pagination';

const getYesNoValue = (value) => {
  const normalizedValue = String(value).trim().toLowerCase();

  if (value === true || normalizedValue === 'true') return 'Yes';
  if (value === false || normalizedValue === 'false') return 'No';

  return null;
};

const renderServiceStatus = (value, tooltipId) => {
  const label = getYesNoValue(value);

  if (!label) return '—';

  return (
    <span
      className={`heartbeat-service-status-capsule ${label === 'Yes' ? 'is-yes' : 'is-no'}`}
      tabIndex={0}
      aria-describedby={tooltipId}
    >
      {label}
      <span className="heartbeat-status-tooltip" id={tooltipId} role="tooltip">
        <span className="heartbeat-status-tooltip-title">Device monitoring</span>
        <span className="heartbeat-status-tooltip-body">
          Yes means the device is monitoring the screen. No means it is not.
        </span>
      </span>
    </span>
  );
};

const getNotificationLabel = (value) => {
  const trimmedValue = String(value ?? '').trim();

  if (!trimmedValue) return null;
  if (trimmedValue.toLowerCase() === 'allowed') return 'Allowed';

  return `${trimmedValue.charAt(0).toUpperCase()}${trimmedValue.slice(1)}`;
};

const renderNotificationStatus = (value) => {
  const label = getNotificationLabel(value);

  if (!label) return '—';

  return (
    <span className={`heartbeat-notification-status-capsule ${label === 'Allowed' ? 'is-allowed' : 'is-not-allowed'}`}>
      {label}
    </span>
  );
};

const DeviceTable = () => {
  const { token } = useAuth();
  const { selectedSiteId } = useSite();

  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [canFetchUsers, setCanFetchUsers] = useState(false);
  const dropdownRef = useRef(null);
  const {
    users: userList,
    loading: loadingList,
    error: errorList
  } = useUserList(searchQuery, selectedSiteId, { enabled: canFetchUsers, delay: 300 });

  const [dateRange, setDateRange] = useState('1');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'lastOnline', direction: 'desc' });
  const [error, setError] = useState(null);

  // --- Fetch devices (explicit on Apply) ---
  const fetchDevices = async (pageToFetch = 1) => {
    if (!searchQuery.trim()) {
      setError('Please enter a username to search.');
      setDevices([]);
      setTotalPages(1);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await deviceService.getMobileHeartbeatByUser({
        interval: dateRange,
        transgressorId: searchQuery,
        authToken: token,
      });

      const data = Array.isArray(response) ? response : [];
      setDevices(data);

      const totalRecords = data.length > 0 ? (data[0].totalDevices || data.length) : 0;
      const calculatedPages = Math.ceil(totalRecords / pageSize);
      setTotalPages(calculatedPages || 1);

      if (data.length === 0) {
        setError('No records found');
      }
    } catch (err) {
      setError('No records found.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCanFetchUsers(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.length < 3) setCanFetchUsers(false);
  }, [searchQuery]);

  const handleSelectUser = (user) => {
    setSearchQuery(user.username);
    setCanFetchUsers(false);
    setCurrentPage(1);
  };

  const handleSort = (columnKey) => {
    const isSameColumn = sortConfig.key === columnKey;
    const direction = isSameColumn && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: columnKey, direction });
  };

  const renderSortArrow = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return <FaSort className="reports-font-style-grey" />;
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchDevices(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1) newPage = 1;
    if (newPage > totalPages) newPage = totalPages;
    setCurrentPage(newPage);
  };

  const sortedDevices = [...devices].sort((a, b) => {
    const valA = a[sortConfig.key] || '';
    const valB = b[sortConfig.key] || '';
    if (sortConfig.key === 'lastOnline' || sortConfig.key === 'forcedStopDate') {
      return sortConfig.direction === 'asc'
        ? new Date(valA) - new Date(valB)
        : new Date(valB) - new Date(valA);
    }
    if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const startIndex = (currentPage - 1) * pageSize;
  const pagedDevices = sortedDevices.slice(startIndex, startIndex + pageSize);

  const handleExportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Devices');

      worksheet.columns = [
        { header: 'Group', key: 'group', width: 22 },
        { header: 'Device ID', key: 'visorId', width: 18 },
        { header: 'Manufacturer', key: 'manufacturer', width: 16 },
        { header: 'Phone Model', key: 'model', width: 18 },
        { header: 'OS Version', key: 'os', width: 14 },
        { header: 'Forced Stop', key: 'forcedStop', width: 12 },
        { header: 'Forced Stop Date', key: 'forcedStopDate', width: 20 },
        { header: 'eSafe Version', key: 'version', width: 14 },
        { header: 'Last Online', key: 'lastOnline', width: 22 },
        { header: 'Device Monitoring On?', key: 'captureServiceRunning', width: 22 },
        { header: 'Notifications', key: 'notifications', width: 14 }
      ];

      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };

      sortedDevices.forEach((item) => {
        const forcedStopDate =
          item.forcedStopDate && item.forcedStopDate !== '0001-01-01T00:00:00'
            ? new Date(item.forcedStopDate).toLocaleString()
            : '';

        worksheet.addRow({
          group: item.groupName || '',
          visorId: item.visorId || '',
          manufacturer: item.manufacturer || '',
          model: item.model || '',
          os: item.os ? `Android ${item.os}` : '',
          forcedStop: item.forcedStop ? 'Yes' : 'No',
          forcedStopDate: forcedStopDate,
          version: item.version || '',
          lastOnline: item.lastOnline ? new Date(item.lastOnline).toLocaleString() : '',
          captureServiceRunning: getYesNoValue(item.captureServiceRunning) ?? '',
          notifications: getNotificationLabel(item.notifications) ?? ''
        });
      });

      worksheet.autoFilter = {
        from: 'A1',
        to: `K${sortedDevices.length + 1}`
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      saveAs(blob, `device_report_${stamp}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export Excel file. Please try again.');
    }
  };

  return (
    <div>
      <InformationSection filePath="md/heartbeat.md" />

      <div className="report-advanced-search">
        <div className="report-search-group">
          <div className="report-search-input-group">
            <span className="report-search-input-group-text">
              <FaSearch />
            </span>

            <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
              <input
                type="text"
                placeholder="Search users"
                value={searchQuery}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchQuery(val);
                  setCanFetchUsers(val.length >= 3);
                }}
                className="report-search-form-control"
                autoComplete="off"
              />

              {canFetchUsers && searchQuery.length >= 3 && (
                <ul className="user-dropdown">
                  {loadingList && <li className="loading">Loading…</li>}
                  {errorList && <li className="error">Error: {errorList.message}</li>}
                  {!loadingList && !errorList && userList.length === 0 && (
                    <li className="no-results">No users found</li>
                  )}
                  {!loadingList && !errorList && userList.map((u) => (
                    <li key={u.id} onClick={() => handleSelectUser(u)}>
                      {u.username}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="date-select"
          >
            <option value="1">24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 3 months</option>
            <option value="180">Last 6 months</option>
            <option value="365">Last 12 months</option>
          </select>

          <Button
          onClick={handleApplyFilters}>
            Search
          </Button>

          <Button
            buttonStyle="btn-alternate"
            onClick={handleExportExcel}
            title="Export all rows to Excel"
            style={{ marginLeft: 'auto' }}
          >
            Export XLSX
          </Button>
        </div>
      </div>

      {error && (
        <div className="report-error-card">
          <div className="no-records-message">{error}</div>
        </div>
      )}

      <div className="table-custom-container">
        <div className="report-table-section">
          {isLoading && <LoadingSpinner />}
          {!isLoading && (
            <div className="report-table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('groupName')}>
                      Group {renderSortArrow('groupName')}
                    </th>
                    <th onClick={() => handleSort('visorId')}>
                      Device ID {renderSortArrow('visorId')}
                    </th>
                    <th onClick={() => handleSort('manufacturer')}>
                      Manufacturer {renderSortArrow('manufacturer')}
                    </th>
                    <th onClick={() => handleSort('model')}>
                      Phone Model {renderSortArrow('model')}
                    </th>
                    <th onClick={() => handleSort('os')}>
                      OS Version {renderSortArrow('os')}
                    </th>
                    <th onClick={() => handleSort('forcedStop')}>
                      Forced Stop {renderSortArrow('forcedStop')}
                    </th>
                    <th onClick={() => handleSort('forcedStopDate')}>
                      Forced Stop Date {renderSortArrow('forcedStopDate')}
                    </th>
                    <th onClick={() => handleSort('version')}>
                      eSafe Version {renderSortArrow('version')}
                    </th>
                    <th onClick={() => handleSort('lastOnline')}>
                      Date/Time {renderSortArrow('lastOnline')}
                    </th>
                    <th onClick={() => handleSort('captureServiceRunning')}>
                      Device Monitoring On? {renderSortArrow('captureServiceRunning')}
                    </th>
                    <th onClick={() => handleSort('notifications')}>
                      Notifications {renderSortArrow('notifications')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDevices.map((item, idx) => (
                    <tr
                      key={idx}
                      className={item.forcedStop ? 'heartbeat-forced-stop-row' : undefined}
                    >
                      <td><FaSitemap className="reports-font-style-grey" /> {item.groupName || '—'}</td>
                      <td><FaAndroid className="reports-font-style-grey" /> {item.visorId || '—'}</td>
                      <td>{item.manufacturer || '—'}</td>
                      <td>{item.model || '—'}</td>
                      <td>{item.os ? `Android ${item.os}` : '—'}</td>
                      <td>
                        {item.forcedStop ? (
                          <span
                            className="heartbeat-forced-stop-capsule"
                            tabIndex={0}
                            aria-describedby={`forced-stop-tooltip-${idx}`}
                          >
                            Yes
                            <span
                              className="heartbeat-status-tooltip"
                              id={`forced-stop-tooltip-${idx}`}
                              role="tooltip"
                            >
                              <span className="heartbeat-status-tooltip-title">Forced stop</span>
                              <span className="heartbeat-status-tooltip-body">
                                Yes means the app has been stopped by the user.
                              </span>
                            </span>
                          </span>
                        ) : 'No'}
                      </td>
                      <td>
                        {item.forcedStopDate && item.forcedStopDate !== '0001-01-01T00:00:00'
                          ? new Date(item.forcedStopDate).toLocaleString()
                          : '—'}
                      </td>
                      <td>{item.version || '—'}</td>
                      <td>{item.lastOnline ? new Date(item.lastOnline).toLocaleString() : '—'}</td>
                      <td>
                        {renderServiceStatus(item.captureServiceRunning, `device-monitoring-tooltip-${idx}`)}
                      </td>
                      <td>{renderNotificationStatus(item.notifications)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalRecords={devices.length}
          docked
        />
      </div>
    </div>
  );
};

export default DeviceTable;
